import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaSearch, FaTimes, FaCog } from "react-icons/fa";

const TMDB_POSTER_BASE_URL = 'https://image.tmdb.org/t/p/w500';

const MovieRecommendationPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [pickedMovies, setPickedMovies] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recommendationLoading, setRecommendationLoading] = useState(false);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [error, setError] = useState("");
  
  // Weights state
  const [weights, setWeights] = useState({
    genre: 0.5,
    director: 0.5,
    actor: 0.5,
    rating: 0.5,
  });
  
  // Temporary weights for the modal (only applied on "Apply" click)
  const [tempWeights, setTempWeights] = useState({ ...weights });

  // Debounced search effect
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(() => {
      handleSearch();
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Search for movies
  const handleSearch = async () => {
    setLoading(true);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_SERVER_URI}/search/title?q=${encodeURIComponent(searchQuery)}`
      );
      
      // Filter out movies that are already picked
      const filteredResults = (response.data.movies || []).filter(
        (movie) => !pickedMovies.some((picked) => picked.tmdbId === movie.tmdbId)
      );
      
      setSearchResults(filteredResults);
    } catch (err) {
      console.error("Search error:", err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Add movie to picked list
  const addMovie = (movie) => {
    setPickedMovies((prev) => [...prev, movie]);
    // Remove from search results
    setSearchResults((prev) => prev.filter((m) => m.tmdbId !== movie.tmdbId));
    // Clear any previous errors
    setError("");
  };

  // Remove movie from picked list
  const removeMovie = (tmdbId) => {
    setPickedMovies((prev) => prev.filter((movie) => movie.tmdbId !== tmdbId));
    // If the removed movie matches current search, re-run search to show it again
    if (searchQuery.trim()) {
      setTimeout(handleSearch, 100);
    }
    // Clear any previous errors
    setError("");
  };

  // Get recommendations
  const getRecommendations = async () => {
    if (pickedMovies.length === 0) return;

    setRecommendationLoading(true);
    setError("");

    try {
      const token = localStorage.getItem('accessToken');
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_SERVER_URI}/recommend/openai`,
        {
          movie_ids: pickedMovies.map((m) => m.tmdbId),
          weights: weights,
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      setRecommendations(response.data.recommendations || []);
      setShowRecommendations(true);
      
      // Trigger a custom event to refresh token count in navbar
      window.dispatchEvent(new Event('tokenChange'));
    } catch (err) {
      console.error("Recommendation error:", err);
      
      // Display specific error message from API
      if (err.response?.status === 403) {
        // Insufficient tokens error
        setError(err.response.data?.message || "You don't have enough tokens to get recommendations. Please check your token balance.");
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError("Failed to get recommendations. Please try again.");
      }
    } finally {
      setRecommendationLoading(false);
    }
  };

  const handleBackToSearch = () => {
    setShowRecommendations(false);
    setRecommendations([]);
  };

  // Preferences handlers
  const openPreferences = () => {
    setTempWeights({ ...weights });
    setShowPreferences(true);
  };

  const closePreferences = () => {
    setShowPreferences(false);
  };

  const applyWeights = () => {
    setWeights({ ...tempWeights });
    setShowPreferences(false);
  };

  const handleSliderChange = (key, value) => {
    setTempWeights((prev) => ({
      ...prev,
      [key]: parseFloat(value),
    }));
  };

  // Recommendations view
  if (showRecommendations) {
    return (
      <div className="min-h-screen bg-white py-8">
        <div className="max-w-2xl mx-auto px-4">
          <button
            onClick={handleBackToSearch}
            className="mb-6 text-gray-600 hover:text-gray-900 transition-colors"
          >
            ← Back to search
          </button>

          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Your Recommendations
          </h2>
          <p className="text-gray-600 mb-8">
            Based on your {pickedMovies.length} selected {pickedMovies.length === 1 ? 'movie' : 'movies'}
          </p>

          <div className="space-y-4">
            {recommendations.map((movie) => (
              <div
                key={movie.tmdbId}
                className="flex gap-4 bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all"
              >
                <div className="w-24 h-36 flex-shrink-0">
                  {movie.posterPath ? (
                    <img
                      src={`${TMDB_POSTER_BASE_URL}${movie.posterPath}`}
                      alt={movie.title}
                      className="w-full h-full object-cover rounded"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-200 rounded flex items-center justify-center">
                      <span className="text-gray-400 text-xs">No Image</span>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {movie.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-2">{movie.year}</p>
                  {movie.overview && (
                    <p className="text-sm text-gray-600 line-clamp-3">
                      {movie.overview}
                    </p>
                  )}
                  {movie.voteAverage && (
                    <div className="mt-2 text-xs text-gray-500">
                      ⭐ {movie.voteAverage.toFixed(1)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Main search view
  return (
    <div className="min-h-screen bg-white py-8 pb-32">
      <div className="max-w-2xl mx-auto px-4">
        {/* Zone 1 - Search Bar */}
        <div className="mb-6">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for movies..."
                className="w-full pl-12 pr-4 py-4 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all"
              />
            </div>
            <button
              onClick={openPreferences}
              className="px-5 py-4 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all flex items-center gap-2"
            >
              <FaCog className="text-gray-600" />
              <span className="text-sm font-medium text-gray-900">Preferences</span>
            </button>
          </div>
        </div>

        {/* Preferences Modal */}
        {showPreferences && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">Recommendation Preferences</h3>
                <button
                  onClick={closePreferences}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="space-y-6">
                {/* Genre Slider */}
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-sm font-medium text-gray-900">Genre</label>
                    <span className="text-sm text-gray-600">{tempWeights.genre.toFixed(2)}</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={tempWeights.genre}
                    onChange={(e) => handleSliderChange('genre', e.target.value)}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-900"
                  />
                </div>

                {/* Director Slider */}
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-sm font-medium text-gray-900">Director</label>
                    <span className="text-sm text-gray-600">{tempWeights.director.toFixed(2)}</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={tempWeights.director}
                    onChange={(e) => handleSliderChange('director', e.target.value)}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-900"
                  />
                </div>

                {/* Actor Slider */}
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-sm font-medium text-gray-900">Actor</label>
                    <span className="text-sm text-gray-600">{tempWeights.actor.toFixed(2)}</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={tempWeights.actor}
                    onChange={(e) => handleSliderChange('actor', e.target.value)}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-900"
                  />
                </div>

                {/* Rating Slider */}
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-sm font-medium text-gray-900">Rating</label>
                    <span className="text-sm text-gray-600">{tempWeights.rating.toFixed(2)}</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={tempWeights.rating}
                    onChange={(e) => handleSliderChange('rating', e.target.value)}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-900"
                  />
                </div>
              </div>

              {/* Apply Button */}
              <button
                onClick={applyWeights}
                className="w-full mt-6 py-3 text-base font-semibold text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-all"
              >
                Apply
              </button>
            </div>
          </div>
        )}

        {/* Zone 2 - Pill Shelf */}
        {pickedMovies.length > 0 && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex flex-wrap gap-2">
              {pickedMovies.map((movie) => (
                <div
                  key={movie.tmdbId}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-900 rounded-full border border-blue-300"
                >
                  <span className="text-sm font-medium">{movie.title}</span>
                  <button
                    onClick={() => removeMovie(movie.tmdbId)}
                    className="hover:bg-blue-200 rounded-full p-1 transition-colors"
                  >
                    <FaTimes className="text-xs" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {pickedMovies.length === 0 && (
          <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
            <p className="text-gray-500 text-sm">No movies picked yet</p>
          </div>
        )}

        {/* Error Message Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
              <button
                onClick={() => setError("")}
                className="flex-shrink-0 text-red-400 hover:text-red-600 transition-colors"
              >
                <FaTimes />
              </button>
            </div>
          </div>
        )}

        {/* Zone 3 - Results List */}
        {loading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        )}

        {!loading && searchResults.length > 0 && (
          <div className="space-y-3">
            {searchResults.map((movie) => (
              <div
                key={movie.tmdbId}
                onClick={() => addMovie(movie)}
                className="flex gap-4 bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md hover:border-gray-300 cursor-pointer transition-all"
              >
                <div className="w-16 h-24 flex-shrink-0">
                  {movie.posterPath ? (
                    <img
                      src={`${TMDB_POSTER_BASE_URL}${movie.posterPath}`}
                      alt={movie.title}
                      className="w-full h-full object-cover rounded"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-200 rounded flex items-center justify-center">
                      <span className="text-gray-400 text-xs">No Image</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">
                      {movie.title}
                    </h3>
                    <p className="text-sm text-gray-600">{movie.year}</p>
                  </div>
                  <button className="px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
                    Add
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && searchQuery && searchResults.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">No movies found</p>
          </div>
        )}
      </div>

      {/* CTA Button - Fixed at bottom */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 py-4 px-4">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={getRecommendations}
            disabled={pickedMovies.length === 0 || recommendationLoading}
            className="w-full py-4 text-lg font-semibold text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-all disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {recommendationLoading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Getting recommendations...
              </span>
            ) : pickedMovies.length === 0 ? (
              "Get recommendations"
            ) : (
              `Get recommendations (${pickedMovies.length} picked)`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MovieRecommendationPage;
